import React, { useState, useEffect } from 'react';
import {
  Box,
  Alert,
  CircularProgress,
  Autocomplete,
  createFilterOptions,
  TextField,
  Chip,
} from '@mui/material';
import { PersonAdd as PersonAddIcon, Groups as GroupsIcon } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import i18n from '../../i18n/i18n-setup';
import { apiService } from '../../services/api.service';
import { SegmentedControl } from '../ui/SegmentedControl';
import { InitialsAvatar } from '../ui/InitialsAvatar';
import { ContestFormDialogLayout, FormFooterButtons, PersonOptionRow } from './ContestFormDialogLayout';

interface Participant {
  userId: string;
  name: string;
  username: string;
  stravaProfile?: {
    firstname?: string;
    lastname?: string;
    profile?: string;
  };
}

interface ParticipantFormProps {
  contestId: string;
  onSave: (data: { participantId: string } | { participantIds: string[] }) => void;
  onCancel: () => void;
}

const getDisplayName = (participant: Participant): string => {
  if (participant.stravaProfile?.firstname && participant.stravaProfile?.lastname) {
    return `${participant.stravaProfile.firstname} ${participant.stravaProfile.lastname}`;
  }
  return participant.name || participant.username || i18n.t('contests:unknownUser');
};

const getSecondaryInfo = (participant: Participant): string => (participant.username ? `@${participant.username}` : '');

// Match on display name and username (accent-insensitive), not just the display label
const filterParticipants = createFilterOptions<Participant>({
  stringify: (participant) => [getDisplayName(participant), participant.username].filter(Boolean).join(' '),
});

export const ParticipantForm: React.FC<ParticipantFormProps> = ({ contestId, onSave, onCancel }) => {
  const { t } = useTranslation('contests');
  const [selectedParticipant, setSelectedParticipant] = useState<Participant | null>(null);
  const [selectedParticipants, setSelectedParticipants] = useState<Participant[]>([]);
  const [availableParticipants, setAvailableParticipants] = useState<Participant[]>([]);
  const [loading, setLoading] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const [multiSelectMode, setMultiSelectMode] = useState(false);
  const [error, setError] = useState(false);

  // Load available participants when component mounts
  useEffect(() => {
    const loadAvailableParticipants = async () => {
      try {
        setLoading(true);
        setError(false);
        const participants = await apiService.getAvailableContestParticipants(contestId);
        setAvailableParticipants(participants);
      } catch (error) {
        console.error('Error loading available participants:', error);
        setError(true);
      } finally {
        setLoading(false);
      }
    };

    loadAvailableParticipants();
  }, [contestId]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (multiSelectMode) {
      if (selectedParticipants.length === 0) {
        return; // Don't submit if no participants selected
      }
      onSave({ participantIds: selectedParticipants.map(participant => participant.userId) });
    } else {
      if (!selectedParticipant) {
        return; // Don't submit if no participant selected
      }
      onSave({ participantId: selectedParticipant.userId });
    }
  };

  const handleModeToggle = (checked: boolean) => {
    setMultiSelectMode(checked);
    // Clear selections when switching modes
    setSelectedParticipant(null);
    setSelectedParticipants([]);
  };

  if (error) {
    return (
      <ContestFormDialogLayout
        title={t('participantForm.title')}
        onClose={onCancel}
        footer={<FormFooterButtons onCancel={onCancel} cancelLabel={t('common:actions.close')} hideSubmit />}
      >
        <Alert severity="error">{t('participantForm.loadError')}</Alert>
      </ContestFormDialogLayout>
    );
  }

  const renderInput = (label: string, placeholder: string) => (params: any) => (
    <TextField
      {...params}
      label={label}
      placeholder={placeholder}
      slotProps={{
        input: {
          ...params.InputProps,
          endAdornment: (
            <>
              {loading ? <CircularProgress color="inherit" size={20} /> : null}
              {params.InputProps.endAdornment}
            </>
          ),
        },
      }}
    />
  );

  const renderOption = (props: React.HTMLAttributes<HTMLLIElement> & { key?: React.Key }, option: Participant) => {
    const { key, ...rest } = props;
    return (
      <li key={option.userId} {...rest}>
        <PersonOptionRow name={getDisplayName(option)} secondary={getSecondaryInfo(option)} />
      </li>
    );
  };

  const noneAvailable = availableParticipants.length === 0 && !loading;

  return (
    <Box component="form" onSubmit={handleSubmit}>
      <ContestFormDialogLayout
        title={t('participantForm.title')}
        subtitle={t('participantForm.subtitle')}
        onClose={onCancel}
        footer={
          <FormFooterButtons
            onCancel={onCancel}
            submitIcon={<PersonAddIcon />}
            submitDisabled={
              loading ||
              availableParticipants.length === 0 ||
              (multiSelectMode ? selectedParticipants.length === 0 : !selectedParticipant)
            }
            submitLabel={
              multiSelectMode
                ? t('participantForm.addCount', { count: selectedParticipants.length })
                : t('participantForm.addOne')
            }
          />
        }
      >
        {noneAvailable ? (
          <Alert severity="info">{t('participantForm.noneAvailable')}</Alert>
        ) : (
          <>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              <Box component="span" sx={{ fontSize: 14, fontWeight: 500 }}>{t('participantForm.selectionMode')}</Box>
              <SegmentedControl
                ariaLabel={t('participantForm.selectionMode')}
                value={multiSelectMode ? 'multiple' : 'single'}
                onChange={(v) => handleModeToggle(v === 'multiple')}
                options={[
                  { value: 'single', label: t('participantForm.single'), icon: <PersonAddIcon /> },
                  { value: 'multiple', label: t('participantForm.multiple'), icon: <GroupsIcon /> },
                ]}
              />
            </Box>

            {multiSelectMode ? (
              <Autocomplete
                multiple
                id="participants-multi-select"
                options={availableParticipants}
                value={selectedParticipants}
                onChange={(_, newValue) => setSelectedParticipants(newValue)}
                inputValue={inputValue}
                onInputChange={(_, newInputValue) => setInputValue(newInputValue)}
                getOptionLabel={(option) => getDisplayName(option)}
                filterOptions={filterParticipants}
                isOptionEqualToValue={(option, value) => option.userId === value.userId}
                loading={loading}
                renderTags={(value, getTagProps) =>
                  value.map((option, index) => {
                    const { key, ...tagProps } = getTagProps({ index });
                    return (
                      <Chip
                        key={option.userId}
                        avatar={<InitialsAvatar name={getDisplayName(option)} size={22} />}
                        label={getDisplayName(option)}
                        {...tagProps}
                        size="small"
                      />
                    );
                  })
                }
                renderInput={renderInput(
                  t('participantForm.selectMany'),
                  selectedParticipants.length === 0 ? t('participantForm.searchMany') : t('participantForm.addMore')
                )}
                renderOption={renderOption}
              />
            ) : (
              <Autocomplete
                id="participant-select"
                options={availableParticipants}
                value={selectedParticipant}
                onChange={(_, newValue) => setSelectedParticipant(newValue)}
                inputValue={inputValue}
                onInputChange={(_, newInputValue) => setInputValue(newInputValue)}
                getOptionLabel={(option) => getDisplayName(option)}
                filterOptions={filterParticipants}
                isOptionEqualToValue={(option, value) => option.userId === value.userId}
                loading={loading}
                renderInput={renderInput(t('participantForm.selectOne'), t('participantForm.searchOne'))}
                renderOption={renderOption}
              />
            )}

            {multiSelectMode && selectedParticipants.length > 0 && (
              <Box sx={{ fontSize: 13, color: 'var(--muted)' }}>
                {t('participantForm.selectedCount', { count: selectedParticipants.length })}
              </Box>
            )}
          </>
        )}
      </ContestFormDialogLayout>
    </Box>
  );
};
